/**
 * ID-buffer picking. The pick scene (every pass's PICK variant) is rendered on demand into a 1x1 RGBA8 target with a
 * sub-frustum projection covering the pointer's pixel (camera.ts subFrustum), then read back. Hover runs at ~30 Hz.
 * Priority: ribbons push their ids back by a small depth bias so an atom over a ribbon wins (ToolsController.cs:100-105).
 */
import { Matrix4, RGBAFormat, UnsignedByteType, WebGLRenderTarget, type Camera, type Scene, type WebGLRenderer } from 'three';
import { subFrustum } from './camera';
import { decodePickId, pickIdFromBytes, type PickObject } from './pick-id';

export const HOVER_INTERVAL_MS = 33; // ~30 Hz

export interface PickHit { object: PickObject; index: number }

/** Lowest-free-slot allocator for PickObjects. */
export class PickSlots {
  private readonly table: (PickObject | undefined)[] = [undefined];
  allocate(entrySerial: number, componentSerial: number, kind: PickObject['kind']): PickObject {
    let slot = this.table.indexOf(undefined, 1);
    if (slot < 0) slot = this.table.length;
    const o: PickObject = { slot, entrySerial, componentSerial, kind };
    this.table[slot] = o;
    return o;
  }
  free(slot: number): void { if (slot > 0 && slot < this.table.length) this.table[slot] = undefined; }
  get(slot: number): PickObject | undefined { return this.table[slot]; }
}

export class PickPass {
  private readonly target = new WebGLRenderTarget(1, 1, { format: RGBAFormat, type: UnsignedByteType, depthBuffer: true, stencilBuffer: false });
  private readonly pixel = new Uint8Array(4);
  private readonly proj = new Matrix4();

  constructor(private readonly slots: PickSlots) {}

  /**
   * Pick at pixel (px, py) measured from the top-left of a viewport of (width, height) pixels, with `projection`
   * the full-frame projection. `prepare` lets the caller set per-target uniforms (uResolution = 1x1).
   */
  pick(renderer: WebGLRenderer, scene: Scene, camera: Camera, projection: Matrix4, px: number, py: number, width: number, height: number, prepare: (w: number, h: number) => void): PickHit | null {
    const x = Math.floor(px), y = Math.floor(height - py - 1);
    if (x < 0 || y < 0 || x >= width || y >= height) return null;
    subFrustum(projection, x, y, 1, 1, width, height, this.proj);
    const saved = camera.projectionMatrix.clone();
    camera.projectionMatrix.copy(this.proj);
    camera.projectionMatrixInverse.copy(this.proj).invert();
    prepare(1, 1);
    const prevTarget = renderer.getRenderTarget();
    const prevClear = renderer.getClearAlpha();
    renderer.setRenderTarget(this.target);
    renderer.setClearColor(0x000000, 0);
    renderer.clear(true, true, true);
    renderer.render(scene, camera);
    renderer.readRenderTargetPixels(this.target, 0, 0, 1, 1, this.pixel);
    renderer.setRenderTarget(prevTarget);
    renderer.setClearAlpha(prevClear);
    camera.projectionMatrix.copy(saved);
    camera.projectionMatrixInverse.copy(saved).invert();
    const id = pickIdFromBytes(this.pixel[0]!, this.pixel[1]!, this.pixel[2]!, this.pixel[3]!);
    const d = decodePickId(id);
    if (!d) return null;
    const object = this.slots.get(d.slot);
    return object ? { object, index: d.index } : null;
  }

  dispose(): void { this.target.dispose(); }
}
